import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    Info,
    LockKeyhole,
    Mail,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const FEATURES = [
    "Van bestelling naar optimale karindeling",
    "Realtime overzicht voor kantoor en kas",
    "Printklare documenten in één workflow",
];

export default function Login() {
    const { login, status } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);

    if (status === "authenticated") {
        const from = location.state?.from;
        const to = from ? `${from.pathname}${from.search || ""}${from.hash || ""}` : "/";
        return <Navigate to={to} replace />;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email.trim(), password);
            const from = location.state?.from;
            const to = from ? `${from.pathname}${from.search || ""}${from.hash || ""}` : "/";
            navigate(to, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail ?? err.message ?? "Inloggen mislukt"));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen lg:grid lg:grid-cols-[minmax(430px,0.92fr)_1.08fr] bg-[#f5f7f4]" data-testid="login-page">
            <section className="hidden lg:flex relative overflow-hidden bg-florefy-950 text-white p-12 xl:p-16 flex-col justify-between min-h-screen">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-florefy-300/15 blur-2xl" />
                    <div className="absolute bottom-[-12rem] left-[-8rem] h-[34rem] w-[34rem] rounded-full border border-white/10" />
                    <div className="absolute bottom-[-7rem] left-[-3rem] h-[24rem] w-[24rem] rounded-full border border-white/[0.07]" />
                    <div className="absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
                </div>

                <div className="relative z-10">
                    <div className="inline-flex bg-white rounded-2xl px-5 py-4 shadow-[0_16px_50px_rgba(0,0,0,0.22)]">
                        <img src="/florefy-logo.png" alt="Florefy Flow" className="w-48 h-auto object-contain" />
                    </div>
                </div>

                <div className="relative z-10 max-w-xl py-16">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-florefy-200 mb-7">
                        <Sparkles className="h-3.5 w-3.5" />
                        Slimme operationele software
                    </div>
                    <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.04] tracking-[-0.055em] text-white">
                        Rust in de planning.<br />Grip op iedere kar.
                    </h1>
                    <p className="mt-6 text-base xl:text-lg leading-relaxed text-white/55 max-w-lg">
                        Florefy Flow brengt bestellingen, karindeling en printwerk samen in één heldere werkomgeving.
                    </p>
                    <div className="mt-9 space-y-3.5">
                        {FEATURES.map((feature) => (
                            <div key={feature} className="flex items-center gap-3 text-sm text-white/75">
                                <span className="h-7 w-7 rounded-lg bg-florefy-300/15 border border-florefy-300/20 flex items-center justify-center">
                                    <CheckCircle2 className="h-4 w-4 text-florefy-200" />
                                </span>
                                {feature}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-white/25">
                    <span>Florefy Flow</span>
                    <span>Gebouwd voor groei</span>
                </div>
            </section>

            <main className="relative min-h-screen flex items-center justify-center px-5 py-10 sm:px-10 lg:px-16 xl:px-24">
                <div className="absolute top-0 right-0 h-72 w-72 rounded-full bg-florefy-100/50 blur-3xl pointer-events-none" />
                <div className="w-full max-w-[470px] relative">
                    <div className="lg:hidden flex justify-center mb-8">
                        <img src="/florefy-logo.png" alt="Florefy Flow" className="w-44 h-auto object-contain" />
                    </div>

                    <div className="bg-white/95 backdrop-blur-sm border border-florefy-900/10 rounded-[28px] p-6 sm:p-10 shadow-[0_24px_70px_rgba(23,61,44,0.10)]">
                        <div className="h-11 w-11 rounded-2xl bg-florefy-50 border border-florefy-100 flex items-center justify-center mb-7">
                            <ShieldCheck className="h-5 w-5 text-florefy-700" />
                        </div>
                        <div className="mb-8">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-florefy-700 mb-2">Veilige werkruimte</div>
                            <h1 className="font-display text-3xl sm:text-[2.15rem] font-bold tracking-[-0.04em] text-ink-900">Welkom terug</h1>
                            <p className="text-sm text-ink-500 mt-2">Log in om verder te gaan met Florefy Flow.</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                            <div>
                                <label className="block text-xs font-semibold text-ink-700 mb-2" htmlFor="email">E-mailadres</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoComplete="username"
                                        autoFocus
                                        placeholder="naam@bedrijf.nl"
                                        className="w-full h-12 pl-11 pr-4 rounded-xl border border-ink-200 bg-white text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:border-florefy-500 focus:ring-4 focus:ring-florefy-100 transition-all"
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <label className="block text-xs font-semibold text-ink-700" htmlFor="password">Wachtwoord</label>
                                    <button
                                        type="button"
                                        onClick={() => setShowForgot(true)}
                                        className="text-[11px] font-medium text-florefy-700 hover:text-florefy-900 transition-colors"
                                        data-testid="forgot-password-link"
                                    >
                                        Wachtwoord vergeten?
                                    </button>
                                </div>
                                <div className="relative">
                                    <LockKeyhole className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
                                    <input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete="current-password"
                                        placeholder="Voer je wachtwoord in"
                                        className="w-full h-12 pl-11 pr-4 rounded-xl border border-ink-200 bg-white text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:border-florefy-500 focus:ring-4 focus:ring-florefy-100 transition-all"
                                        data-testid="login-password-input"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="login-error">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="group w-full h-12 bg-florefy-900 text-white text-sm font-semibold rounded-xl hover:bg-florefy-800 transition-all shadow-[0_10px_24px_rgba(23,61,44,0.2)] hover:shadow-[0_14px_30px_rgba(23,61,44,0.26)] disabled:opacity-60 flex items-center justify-center gap-2"
                                data-testid="login-submit"
                            >
                                {loading ? "Bezig met inloggen…" : "Inloggen"}
                                {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
                            </button>
                        </form>

                        <div className="mt-7 pt-6 border-t border-ink-100 text-[11px] leading-relaxed text-ink-400 text-center">
                            Nog geen account? Vraag de beheerder van je tenant om je toe te voegen.
                        </div>
                    </div>

                    <div className="mt-5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.14em] text-ink-400">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Beveiligde Florefy-omgeving
                    </div>
                </div>
            </main>

            {showForgot && (
                <div className="fixed inset-0 bg-florefy-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowForgot(false)} data-testid="forgot-modal">
                    <div className="bg-white border border-white/50 rounded-[24px] shadow-[0_30px_90px_rgba(11,36,25,0.3)] w-full max-w-md p-7 mx-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="h-11 w-11 rounded-2xl bg-florefy-50 border border-florefy-100 flex items-center justify-center">
                                <Info className="h-5 w-5 text-florefy-700" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-ink-900">Wachtwoord vergeten?</h2>
                                <p className="text-[10px] text-florefy-700 font-semibold uppercase tracking-[0.16em] mt-0.5">Florefy Flow</p>
                            </div>
                        </div>
                        <p className="text-sm leading-relaxed text-ink-700 mb-2">
                            Om je wachtwoord te resetten, neem je contact op met je <strong>beheerder</strong> (de persoon die je account heeft aangemaakt).
                        </p>
                        <p className="text-sm leading-relaxed text-ink-700 mb-5">
                            Hij of zij kan je een nieuw tijdelijk wachtwoord geven. Na het inloggen kun je dat zelf wijzigen via <em>Mijn account → Wachtwoord wijzigen</em>.
                        </p>
                        <div className="bg-florefy-50 border border-florefy-100 rounded-xl px-4 py-3 text-xs text-florefy-900 mb-6">
                            <strong>Tip:</strong> bel of mail je contactpersoon — niet de algemene support.
                        </div>
                        <div className="flex justify-end">
                            <button type="button" onClick={() => setShowForgot(false)} className="px-5 py-2.5 rounded-xl bg-florefy-900 text-white text-sm font-semibold hover:bg-florefy-800 shadow-sm" data-testid="btn-close-forgot">
                                Begrepen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
